"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.digitalWorkerOpenApiDocs = void 0;
const ProxyRequestBody = {
    type: 'object',
    description: 'Proxy request body',
    properties: {
        pubKey: {
            type: 'string',
            description: 'Public key of the digital worker. Used to resolve the parent user through the Digital Worker service API.',
            example: 'abc123-def456-ghi789',
            required: true
        },
        payload: {
            type: 'object',
            description: 'The JSON payload to forward to the target controller method. Structure depends on the proxied route.',
            additionalProperties: true,
            example: { name: 'My Avatar', description: 'A customer support bot' },
            required: true
        },
        route: {
            type: 'string',
            description: 'Target API route path to proxy the request to (must be in the allowed paths list, e.g. "/api/avatar").',
            example: '/api/avatar',
            required: true
        },
        method: {
            type: 'string',
            description: 'HTTP method override for the proxied request. If omitted, the original request method (POST) is used.',
            enum: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
            example: 'POST',
            required: false
        }
    },
    required: ['pubKey', 'payload', 'route']
};
const ProxyUploadRequestBody = {
    type: 'object',
    description: 'Proxy upload request body (multipart/form-data)',
    properties: {
        pubKey: {
            type: 'string',
            description: 'Public key of the digital worker. Used to resolve the parent user through the Digital Worker service API.',
            example: 'abc123-def456-ghi789',
            required: true
        },
        payload: {
            type: 'string',
            description: 'JSON-encoded string of the payload to forward to the target controller method. Will be parsed as JSON on the server side.',
            example: '{"name": "My Avatar", "description": "A customer support bot"}',
            required: false
        },
        route: {
            type: 'string',
            description: 'Target API route path to proxy the upload to (must be in the allowed paths list).',
            example: '/api/avatar',
            required: true
        },
        method: {
            type: 'string',
            description: 'HTTP method override for the proxied request.',
            enum: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
            example: 'POST',
            required: false
        },
        file: {
            type: 'string',
            format: 'binary',
            description: 'File to upload. Sent as a multipart form field named "file".',
            required: true
        }
    },
    required: ['pubKey', 'route', 'file']
};
const CreateUserRequestBody = {
    type: 'object',
    description: 'Create digital worker user request body',
    properties: {
        login: {
            type: 'string',
            description: 'Login / username for the new digital worker user. Must be unique across the system.',
            example: 'dw_bot_support_01',
            required: true
        },
        email: {
            type: 'string',
            format: 'email',
            description: 'Email address for the new digital worker user. Must be unique across the system.',
            example: 'bot-support-01@example.com',
            required: true
        }
    },
    required: ['login', 'email']
};
const ErrorResponse = {
    type: 'object',
    properties: {
        success: { type: 'boolean', example: false },
        error: { type: 'string' }
    }
};
const UserResponse = {
    type: 'object',
    description: 'User object',
    properties: {
        id: {
            type: 'string',
            description: 'User unique identifier'
        },
        username: {
            type: 'string',
            description: 'User login / username'
        },
        apiKey: {
            type: 'string',
            description: 'API key associated with the user'
        }
    }
};
exports.digitalWorkerOpenApiDocs = {
    'digital-worker.proxy': {
        description: 'Proxy an API request on behalf of a digital worker user. Authenticates via a public key that resolves to a parent user through the Digital Worker service, then forwards the payload to the specified internal API route. Only whitelisted routes are allowed.',
        payload: {
            body: ProxyRequestBody
        },
        responses: {
            200: {
                description: 'Proxied request executed successfully. The response body depends on the target route being proxied.',
                returnParams: {
                    type: 'object',
                    description: 'Response from the target controller method (varies by route)',
                    additionalProperties: true
                }
            },
            403: {
                description: 'Access denied — user not found for the given pubKey or route not in the allowed paths list',
                returnParams: ErrorResponse
            },
            404: {
                description: 'Target route not found in the application route registry',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Proxy action failed due to an internal error',
                returnParams: ErrorResponse
            }
        }
    },
    'digital-worker.proxy-upload': {
        description: 'Proxy a file upload API request on behalf of a digital worker user. Accepts multipart/form-data with a file field and an optional JSON-encoded payload string. Authenticates via a public key, then forwards the file and payload to the specified internal API route. Only whitelisted routes are allowed.',
        payload: {
            body: ProxyUploadRequestBody
        },
        responses: {
            200: {
                description: 'Proxied upload request executed successfully. The response body depends on the target route being proxied.',
                returnParams: {
                    type: 'object',
                    description: 'Response from the target controller method (varies by route)',
                    additionalProperties: true
                }
            },
            400: {
                description: 'No file provided for proxy upload',
                returnParams: ErrorResponse
            },
            403: {
                description: 'Access denied — user not found for the given pubKey or route not in the allowed paths list',
                returnParams: ErrorResponse
            },
            404: {
                description: 'Target route not found in the application route registry',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Proxy upload action failed due to an internal error',
                returnParams: ErrorResponse
            }
        }
    },
    'digital-worker.create-user': {
        description: 'Create a new digital worker user under the parent user resolved by the given pubKey. The new user is assigned to the "digital_social" user group (created automatically if it does not exist), inherits the parent\'s company, parent ID, and account balance, and is assigned the free-tier account grade. A random password is generated and hashed; the user is not meant to log in directly.',
        payload: {
            body: CreateUserRequestBody
        },
        responses: {
            200: {
                description: 'User created successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: UserResponse
                    }
                }
            },
            9004: {
                description: 'Bad request — missing or existing required fields (login, email)',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: {
                            type: 'string',
                            example: 'login and email are required'
                        }
                    }
                }
            },
            9005: {
                description: 'Parent user not found for the given pubKey',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: {
                            type: 'string',
                            example: 'Parent user not found for the given pubKey'
                        }
                    }
                }
            },
            500: {
                description: 'Failed to create user due to an internal error',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' }
                    }
                }
            }
        }
    }
};
